<template>
    <div :id="`${mapUnicode}_diagram_container`" class="h-100 w-100 d-flex justify-content-center align-items-center overflow-hidden">
        <div id="_diagram_cover" class="position-relative" :style="generateStyle">
            <template v-if="!imageDisable">
                <b-img v-show="setCoverSizeFinish"
                       :src="imgUrl"
                       class="position-absolute "
                       :style="generateStyle"
                       alt="Background Image"></b-img>
            </template>
            <div :id="mapJson.mapName"
                 class="position-absolute float-upon"
                 :style="generateStyle"
                 :class="setCoverSizeFinish ? '' : 'w-100 h-100'"></div>
        </div>
    </div>
</template>

<script>
import * as echarts from "echarts";
import {_getBoundingCoords, originLat, originLon} from "@/components/LayoutMaps/openLayers/utils/MapUtils";
import emCommonUtilMixin from "@/mixin/em/common/stuffs/utils/emCommonUtilMixin";

export default {
    name: "CustomizedDiagramView",
    mixins: [emCommonUtilMixin],
    props: {
        mapData: {
            type: Array,
            default: () => []
        },
        imgUrl: {
            type: String,
            default: ""
        },
        zoom: {
            type: Number,
            default: 0.85
        },
        piecesColor: {
            type: Array,
            default: () => [
                {value: 0, color: '#b0b0b0'},
                {value: 1, color: '#53d445'},
                {value: 2, color: '#c8c800'},
                {value: 3, color: '#d00000'},
            ]
        },
        tooltipFormatter: {
            type: Function
        },
        piecesLabelFormatter: {
            type: Function
        },
        piecesLabelStyleRich: {
            type: Object
        },
        regionNames: {
            type: Array,
            default: () => []
        },
        regionStyle: {
            type: Object,
            default: () => {
                return {
                    areaColor: '#b0b0b0',
                    emphasis: {
                        areaColor: '#b0b0b0',
                        shadowBlur: 0
                    }
                }
            }
        },
        scaleAble: {
            type: Boolean,
            default: false
        },
        imageDisable: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        generateStyle() {
            return this.setCoverSizeFinish ? `width: ${this.fitWidth * this.scaleRate}px !important; height: ${this.fitHeight * this.scaleRate}px !important;` : '';
        },
        coverElement() {
            return document.getElementById('_diagram_cover');
        },
        mapUnicode() {
            let mapName = this.mapJson.mapName;
            return mapName ? mapName : this.genNonDuplicateID(4);
        },
    },
    data() {
        return {
            localeMapData: [],
            mapJson: {},
            chart: null,
            chartHasInit: false,
            options: {},
            regions: [],
            boundingCoords: [
                [-originLon, originLat],
                [originLon, -originLat]
            ],
            imgLoadSuccess: false,
            imgLoadFailed: false,
            setCoverSizeFinish: false,
            fitWidth: 0,
            fitHeight: 0,
            imgInfo: {},

            generatedRichHeader: '__',
            labelRich: {
                name: {
                    fontSize: 12,
                    color: '#fff'
                },
                normalName: {
                    fontSize: 12,
                    color: '#222222'
                },
            },

            styleJson: undefined,
            pieceStyleMap: new Map(),
            lastRequestResizeTime: new Date(),

            scaleRate: 1,
            maxScale: 4,
            minScale: 0.5,
            scaleStep: 0.2,
            x: 0,
            y: 0,
            isPointerdown: false, // 按下标识
            diff: {x: 0, y: 0}, // 相对于上一次pointermove移动差值
            lastPointermove: {x: 0, y: 0},

            containerWidth: 0,
            containerHeight: 0,

            scaleEnabled: false,
            enableScaleTryCount: 0,
        }
    },
    watch: {
        mapData: {
            handler() {
                if (this.chart) {
                    this.localeMapData = this.deepCopyObj(this.mapData);
                    this.setPieceStyle();
                    this.$nextTick(() => this.setOptions());
                }
            },
            deep: true
        },
        scaleRate() {
            if (this.styleJson) {
                this.readPieceStyle(this.styleJson);
            }
            this.chartResize();
        },
        scaleAble: {
            handler() {
                this.switchScale();
            },
            immediate: true
        }
    },
    mounted() {
        if (this.piecesLabelStyleRich) {
            this.copyObj2Obj(this.piecesLabelStyleRich, this.labelRich, true);
        }
        this.$nextTick(() => this.switchScale());
    },
    beforeDestroy() {
        this.disableDrag();
        if (this.chart) {
            this.chart.dispose();
            this.chart = null;
        }
    },
    methods: {
        setMapJson(data) {
            this.mapJson = data;
            this.$nextTick(() => this.initChartMap());
        },
        setSomeJson(map, style) {
            this.styleJson = style;
            this.setMapJson(map);
        },
        initChartMap() {
            let tempList = [];
            for (let area of this.regionNames) {
                tempList.push({
                    name: area,
                    itemStyle: this.regionStyle
                });
            }
            this.regions = tempList;

            echarts.registerMap(this.mapUnicode, this.mapJson);
            if (!this.chartHasInit) {
                this.chart = echarts.init(document.getElementById(this.mapJson.mapName));
                this.chart.on('click', 'series', event => this.$emit('pieceClick', event));
                this.chart.on('mouseout', 'series', event => this.whenMouseout(event));
                this.chart.on('mouseover', 'series', event => this.whenMouseOver(event))
                //auto resize
                window.onresize = this.delayResize;
                this.chartHasInit = true;
            }
            if (this.hasCode(this.imgUrl)) {
                _getBoundingCoords(this.imgUrl).then(info => {
                    this.boundingCoords = info.bounding;
                    this.imgInfo = info.info;
                    this.$nextTick(() => this.setChildSize());
                }).catch(() => {
                    this.imgLoadFailed = true;
                    this.$emit('imgLoadFailed');
                    this.$message.error('图片加载失败');
                });
            }
        },
        delayResize() {
            this.lastRequestResizeTime = new Date();
            let temp = this.lastRequestResizeTime.getTime();
            setTimeout(() => {
                if (temp === this.lastRequestResizeTime.getTime()) {
                    this.setChildSize();
                }
            }, 500);
        },
        setChildSize() {
            let element = this.containerElement();
            if (!element) {
                console.log('el', element, this.mapUnicode);
                return;
            }
            this.containerWidth = element.clientWidth;
            this.containerHeight = element.clientHeight;
            let cw = this.containerWidth * this.zoom;
            let ch = this.containerHeight * this.zoom;
            let containerAspectScale = ch / cw;
            if (this.imgInfo.aspectScale) {
                if (this.imgInfo.aspectScale > containerAspectScale) {
                    this.fitHeight = ch;
                    this.fitWidth = this.fitHeight / this.imgInfo.aspectScale;
                } else {
                    this.fitWidth = cw;
                    this.fitHeight = this.fitWidth * this.imgInfo.aspectScale;
                }
            } else {
                this.fitHeight = ch;
                this.fitWidth = cw;
            }
            if (this.styleJson) {
                this.readPieceStyle(this.styleJson);
            }
            this.setOptions();
            this.setCoverSizeFinish = true;
            // this.imgInfo.zoom = this.fitWidth / cw;
            // this.$emit('resizeFinish', this.imgInfo);
            this.chartResize();
        },
        chartResize() {
            this.chart.resize({
                width: this.fitWidth * this.scaleRate,
                height: this.fitHeight * this.scaleRate
            });
        },
        readPieceStyle(styleJson) {
            /*{
                'test_1': {
                    label: {
                        fontSize: 16,
                        color: '#ffffff'
                    },
                    piece: {
                        color: '#ffffff'
                    }
                }
            }*/
            let styles;
            if (typeof styleJson === 'string') {
                styles = JSON.parse(styleJson);
            } else {
                styles = this.deepCopyObj(styleJson);
            }
            for (let name in styles) {
                let obj = styles[name];
                if (obj) {
                    if (obj.label) {
                        let temp = obj.label;
                        let count = 0;//屏蔽为空的配置
                        for (let item in temp) {
                            let val = temp[item];
                            if (val && typeof val === 'number') {
                                temp[item] = this.calFitSize(temp[item]);
                            }
                            count++;
                        }
                        if (count > 0) {
                            //创建预设样式
                            this.labelRich[this.labelRichName(name)] = temp;
                            //创建组合样式
                            for (let id in this.labelRich) {
                                if (!id.startsWith(this.generatedRichHeader)) {
                                    let item = this.deepCopyObj(this.labelRich[id]);
                                    this.copyObj2Obj(temp, item, true);
                                    this.labelRich[this.labelRichName(name + '_' + id)] = item;
                                }
                            }
                        }
                    }
                    if (obj.piece) {
                        this.pieceStyleMap.set(name, obj.piece);
                    }
                }
            }
            // console.log('rich list', this.labelRich);
            this.setPieceStyle();
            this.setOptions();
        },
        setPieceStyle() {
            if (this.pieceStyleMap.size > 0) {
                for (let key of this.pieceStyleMap.keys()) {
                    let style = this.pieceStyleMap.get(key);
                    if (style.color) {
                        let hasData = false;
                        for (let item of this.localeMapData) {
                            if (item.name === key) {
                                hasData = true;
                                item.itemStyle = {
                                    color: style.color
                                }
                                break;
                            }
                        }
                        if (!hasData) {
                            this.localeMapData.push({
                                name: key,
                                itemStyle: {
                                    color: style.color
                                }
                            });
                        }
                    }
                }
            }
        },
        calFitSize(val) {
            return val / this.imgInfo.width * this.fitWidth * this.scaleRate;
        },
        labelRichName(str) {
            return this.generatedRichHeader + str;
        },
        defaultLabelFormatter(_) {
            let pieceName = _.name;
            let pieceRichName = this.labelRichName(pieceName);
            let gotIt = this.labelRich.hasOwnProperty(pieceRichName);
            if (this.piecesLabelFormatter) {
                let result = this.piecesLabelFormatter(_);
                if (result) {
                    if (gotIt) {//当预设样式存在
                        if (result.startsWith('{') && result.endsWith('}')) {//当指定了样式
                            result = `{${pieceRichName}_${result.substring(1)}`;//使用融合样式
                        } else {
                            result = `{${pieceRichName}|${result}}`;//使用预设样式
                        }
                    }
                    return result;
                }
            }
            let richName = 'normalName';
            if (gotIt) {
                richName = pieceRichName;//使用预设样式
            }
            return `{${richName}|${pieceName}}`;
        },
        defaultTooltipFormatter(_) {
            if (this.tooltipFormatter) {
                let result = this.tooltipFormatter(_);
                if (result) {
                    return result;
                }
            }
            return _.name;
        },
        setOptions() {
            this.options = {
                tooltip: {
                    show: true,
                    formatter: this.defaultTooltipFormatter,
                    confine: true,
                    triggerOn: 'none',
                    enterable: true,
                    position: 'top',
                },
                visualMap: {
                    show: false,
                    type: 'piecewise',
                    min: 1,
                    max: 3,
                    pieces: this.piecesColor
                },
                geo: {
                    map: this.mapJson.mapName,
                    roam: false,//鼠标缩放和平移 都不开启
                    zoom: 1,//当前视角缩放比例
                    label: {
                        show: true,
                        position: 'insideTopLeft',
                        formatter: this.defaultLabelFormatter,
                        rich: this.labelRich,
                        overflow: 'truncate'
                    },
                    itemStyle: {
                        color: '#53d445',
                        borderColor: 'rgba(0 ,0 ,0 , 0.2)',
                    },
                    emphasis: {//高亮状态下。。
                        itemStyle: {
                            areaColor: '#cb42f5',
                            shadowOffsetX: 0,
                            shadowOffsetY: 0,
                            shadowBlur: 20,//阴影模糊大小
                            borderWidth: 0,
                            shadowColor: 'rgba(0, 0, 0, 0.5)'
                        }
                    },
                    center: [0, 0],//中心坐标
                    aspectScale: 1,
                    boundingCoords: this.boundingCoords,//显示的区域
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                    regions: this.regions
                },
                series: [{
                    type: 'map',
                    name: this.mapJson.mapName,
                    geoIndex: 0,
                    data: this.localeMapData
                }]
            }
            this.chart.setOption(this.options);
        },
        whenMouseWheel(event) {
            if (event) {
                if (event.altKey) {
                    let ratio = -1;
                    if (event.deltaY < 0 && this.scaleRate < this.maxScale) {
                        ratio = 1 + this.scaleStep;
                    } else if (event.deltaY > 0 && this.scaleRate > this.minScale) {
                        ratio = 1 / (1 + this.scaleStep);
                    }
                    if (ratio > 0) {
                        this.scaleRate *= ratio;
                        let before = this.coverElement.getBoundingClientRect();
                        this.$nextTick(() => {
                            let current = this.coverElement.getBoundingClientRect();
                            let ptLeft = event.pageX - before.x;
                            let ptTop = event.pageY - before.y;
                            let fixX = before.x - ptLeft * (ratio - 1);
                            let fixY = before.y - ptTop * (ratio - 1);
                            this.x += fixX - current.x;
                            this.y += fixY - current.y;
                            this.transform();
                        });
                    }
                } else if (event.deltaY) {
                    this.y += this.calScroll(this.coverElement.offsetTop, this.fitHeight * this.scaleRate, this.containerHeight, event.deltaY, this.y);
                    this.transform();
                } else if (event.deltaX) {
                    let temp = this.calScroll(this.coverElement.offsetLeft, this.fitWidth * this.scaleRate, this.containerWidth, event.deltaX, this.x);
                    this.x += temp;
                    this.transform();
                }
            }
        },
        calScroll(offset, inner, outer, delta, current) {
            let offsetStart = offset + current;
            let offsetEnd = inner + offsetStart - outer;
            if (delta > 0 && offsetStart < 0) {
                if (offsetStart + delta > 0) {
                    return -offsetStart;
                } else {
                    return delta;
                }
            } else if (delta < 0 && offsetEnd > 0) {
                if (offsetEnd + delta < 0) {
                    return -offsetEnd;
                } else {
                    return delta;
                }
            } else {
                return 0;
            }
        },
        enableDrag() {
            let element = this.containerElement();
            console.log('try scale', element, this.scaleEnabled, this.enableScaleTryCount);
            if (!element && this.enableScaleTryCount < 5) {
                this.$nextTick(() => this.switchScale());
                this.enableScaleTryCount++;
            }
            if (!this.scaleEnabled && element) {
                element.addEventListener('wheel', this.whenMouseWheel, {passive: false});
                element.addEventListener('pointerdown', this.whenPointerDown);
                element.addEventListener('pointermove', this.whenPointerMove);
                element.addEventListener('pointerup', this.whenDragFinish);
                element.addEventListener('mouseout', this.whenDragFinish);
                element.addEventListener('pointercancel', this.whenDragFinish);
                document.addEventListener('keydown', this.whenKeyDown);
                this.scaleEnabled = true;
            }
        },
        disableDrag() {
            let element = this.containerElement();
            if (this.scaleEnabled && element) {
                element.removeEventListener('wheel', this.whenMouseWheel);
                element.removeEventListener('pointerdown', this.whenPointerDown);
                element.removeEventListener('pointermove', this.whenPointerMove);
                element.removeEventListener('pointerup', this.whenDragFinish);
                element.removeEventListener('mouseout', this.whenDragFinish);
                element.removeEventListener('pointercancel', this.whenDragFinish);
                document.removeEventListener('keydown', this.whenKeyDown);
                this.scaleEnabled = false;
            }
        },
        whenPointerDown(e) {
            this.isPointerdown = true;
            this.lastPointermove = {x: e.clientX, y: e.clientY};
        },
        whenPointerMove(e) {
            if (this.isPointerdown) {
                const current1 = {x: e.clientX, y: e.clientY};
                this.diff.x = current1.x - this.lastPointermove.x;
                this.diff.y = current1.y - this.lastPointermove.y;
                this.lastPointermove = {x: current1.x, y: current1.y};
                this.x += this.diff.x;
                this.y += this.diff.y;
                this.transform();
            }
            e.preventDefault();
        },
        whenDragFinish() {
            this.isPointerdown = false;
        },
        transform() {
            this.coverElement.style.transform = 'translate3d(' + this.x + 'px, ' + this.y + 'px, 0)';
        },
        whenKeyDown(event) {
            if (event) {
                if (event.code === 'KeyR') {
                    this.cancelScale();
                    event.preventDefault();
                }
            }
        },
        cancelScale() {
            this.scaleRate = 1;
            this.x = 0;
            this.y = 0;
            this.transform();
        },
        switchScale() {
            if (this.scaleAble) {
                this.enableDrag();
            } else {
                this.disableDrag();
            }
        },
        containerElement() {
            return document.getElementById(`${this.mapUnicode}_diagram_container`);
        },
        whenMouseout(event) {
            this.$emit('mouseout', event);
            this.chart.dispatchAction({
                type: 'hideTip'
            })
        },
        whenMouseOver(event) {
            if (event.data && event.data.tooltipText) {
                this.chart.dispatchAction({
                    type: 'showTip',
                    seriesIndex: event.seriesIndex,
                    dataIndex: event.dataIndex,
                });
            }
        },
    }

}
</script>

<style >
.b-toaster{
    z-index: 8999 !important;
}
</style>